import React, { useEffect } from "react";
import { connect } from "react-redux";
import { useBoolean, useNumber } from "react-hanger";
import styled from "styled-components";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Slide from "@mui/material/Slide";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import ReactPlayer from "react-player";
import LiveChat from "react-livechat";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import TextLoop from "react-text-loop";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import "../../../assets/css/app.css";

import { LIVECHAT_LICENSE, STRIPE_PUBLISHABLE_KEY } from "../../../constants";
import { Emphasize } from "../../styles";
import BundleCard from "../../bundleCard";
import { Step1Content, Step2Content, Step3Content, Step4Content } from "./steps";
import FacebookIcon from "../../../assets/images/icon_facebook.webp";
import InstagramIcon from "../../../assets/images/icon_instagram.webp";
import LinkedInIcon from "../../../assets/images/icon_linkedin.webp";
import MyNewsDeskIcon from "../../../assets/images/icon_mynewsdesk.webp";
import TwitterIcon from "../../../assets/images/icon_twitter.webp";
import YoutubeIcon from "../../../assets/images/icon_youtube.webp";
import MockupReport from "../../../assets/images/Mockup_Report-HealthyRelationship.jpg"
import { csActions, visitorActions } from "../../../redux/actions";

const Transition = React.forwardRef((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const HomePage = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const activeStep = useNumber(0);
  const isAvailableBundlesDialogOpen = useBoolean(false);

  const handleShowAvailableBundles = () => {
    isAvailableBundlesDialogOpen.setTrue();
    props.getBundles();
  }

  const handleAvailableBundlesDialogClose = () => {
    isAvailableBundlesDialogOpen.setFalse();
  }

  const handleNextStep = () => {
    activeStep.increase();
  }

  const handleBackStep = () => {
    activeStep.decrease();
  }

  const steps = [
    {
      label: "Upload CliftonStrength 34 Report",
      content: <Step1Content handleNextStep={handleNextStep} handleBackStep={handleBackStep} />,
    },
    {
      label: "Enter email address",
      content: <Step2Content handleNextStep={handleNextStep} handleBackStep={handleBackStep} />,
    },
    {
      label: "Choose bundle",
      content: (
        <Elements stripe={stripePromise}>
          <Step3Content handleNextStep={handleNextStep} handleBackStep={handleBackStep} />
        </Elements>
      ),
    },
    {
      label: "Book coaching session",
      content: <Step4Content handleNextStep={handleNextStep} handleBackStep={handleBackStep} />,
    },
  ];

  useEffect(() => {
    props.getOrCreateVisitorId();
  }, []);

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          height: "80px",
          backgroundColor: "#ffffff",
          color: "rgb(78, 116, 162)",
          boxShadow: "rgba(78, 116, 162, 0.2) 0px 2px 4px -1px, rgba(78, 116, 162, 0.14) 0px 4px 5px 0px, rgba(78, 116, 162, 0.12) 0px 1px 10px 0px",
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            padding: "10px 0",
          }}
        >
          <TextLoop>
            <span>
              <Typography
                sx={{
                  letterSpacing: "4px",
                  fontWeight: 500,
                }}
              >
                ARE YOU AND YOUR PARTNER COMPATIBLE?
              </Typography>
            </span>
            <span>
              <Typography
                sx={{
                  letterSpacing: "4px",
                  fontWeight: 500,
                  fontSize: "32px",
                  fontFamily: "vladimir",
                }}
              >
                HealthyRelationships.coach
              </Typography>
            </span>
            <span>
              <Typography
                sx={{
                  letterSpacing: "4px",
                  fontWeight: 500,
                }}
              >
                POWERED BY CLIFTONSTRENGTHS
              </Typography>
            </span>
          </TextLoop>
        </Container>
      </AppBar>
      <Container
        maxWidth="lg"
        sx={{
          marginTop: "30px",
          textAlign: "center",
        }}
      >
        <Box sx={{ width: "100%", padding: "10px 0" }}>
          <Typography variant="h4">
            Are you and your partner <Emphasize>compatible</Emphasize>?
          </Typography>
        </Box>
        <Box sx={{ width: "100%", padding: "10px 0", marginBottom: "16px" }}>
          <Typography variant="h6">
            <Emphasize>Identify</Emphasize> potential sources of conflict, and learn how to <Emphasize>overcome</Emphasize> them.
          </Typography>
          <Typography variant="subtitle1" sx={{marginTop: "12px"}}>
            Opposites might be said to attract, but common values, common hobbies, and a common purpose, are what really makes long-term romantic relationships work.
          </Typography>
          <Typography variant="subtitle1">
            Identifying and crystallising areas of friction is the first step to resolving them.
          </Typography>
        </Box>
        <ReactPlayer
          url="https://player.vimeo.com/showcase/9530211"
          controls={true}
          class="react-player"
        />
        <Box
          sx={{
            maxWidth: "640px",
            margin: "auto",
          }}
        >
          <Typography variant="h6" sx={{marginTop: "16px"}}>
            <Emphasize>Benefits:</Emphasize>
          </Typography>
          <Box sx={{ textAlign: "left" }}>
            <Typography
              variant="subtitle1"
              sx={{
                paddingLeft: "28px",
                textIndent: "-28px",
              }}
            >
              <CheckIcon color="success" />&nbsp;Greater understanding of why your partner is the way they are
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                paddingLeft: "28px",
                textIndent: "-28px",
              }}
            >
              <CheckIcon color="success" />&nbsp;Ability to separate see your spouse as a human being, independently of their quirks and habits
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                paddingLeft: "28px",
                textIndent: "-28px",
              }}
            >
              <CheckIcon color="success" />&nbsp;Clearer thoughts when seeing a counsellor
            </Typography>
          </Box>
        </Box>
        <Box sx={{ width: "100%", padding: "10px 0" }}>
          <Typography variant="h6" sx={{marginTop: "22px"}}>
            We tailor every relationship coaching workshop to suit your unique strengths - both individually, and as a couple.
          </Typography>
          <Typography variant="subtitle1">
            Whether you're just getting to know each other or you are already in a long term relationship, Gallup-certified CliftonStrengths coach Mark Laudi helps you reaffirm your common ground, identify areas of potential conflict, and create a path to resolve them.
          </Typography>
        </Box>
        <Box sx={{ width: "100%", padding: "10px 0" }}>
          <Typography variant="h6" sx={{marginTop: "22px"}}>
            Is <Emphasize>couple coaching</Emphasize> the same as counselling?
          </Typography>
          <Typography variant="subtitle1">
            No, we do not provide therapy or counselling services. Couple communications coaching is about helping you and your partner be the best versions of who you can be, and communicate in a way that brings out the best in you.
          </Typography>
          <Typography variant="subtitle1">
            <p>
              As a result you will be able to understand your similarities and differences more clearly, to support each other in areas where the other is not strong, and to improve the way you relate to each other.
            </p>
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
            gap: "20px",
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box>
              <Typography variant="h6">
                Why do I need a <Emphasize>CliftonStrengths assessment</Emphasize>?
              </Typography>
              <Typography variant="subtitle1">
                Your CliftonStrengths 34 report gives you greater insight into who your partner truly is, and why they think, speak and act the way they do.
              </Typography>
              <Typography variant="subtitle1">
                <p>
                  It allows you to use a common language that help you see your partner as a human being, independently of their quirks and habits.
                </p>
              </Typography>
            </Box>
            <Box sx={{ marginTop: "30px" }}>
              <Typography variant="h6">
                Get your personalised <Emphasize>compatibility assessment</Emphasize> now!
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1">
                You are just <Emphasize>FOUR STEPS</Emphasize> away from breaking old habits, stepping out of entrenched conversations and finding fresh ways to build your relationship.
              </Typography>
              <Typography variant="subtitle1" sx={{marginTop: "30px"}}>
                Check out available bundles&nbsp;
                <Link
                  underline="hover"
                  onClick={handleShowAvailableBundles}
                  sx={{cursor: "pointer"}}
                >
                  here
                </Link>.
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
            }}
          >
            <img src={MockupReport} alt="sample report" width="100%"/>
          </Box>
        </Box>
        <Container
          maxWidth="lg"
          sx={{
            padding: "20px 0",
          }}
        >
          <Stepper activeStep={activeStep.value} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={index}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    {step.content}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Container>
        <Box
          sx={{
            width: "100%",
            marginTop: "20px",
          }}
        >
          <Typography variant="caption">
            Disclaimer:<br />
            CliftonStrengths, CliftonStrengths Theme Names and Theme descriptions are trademarks of Gallup, Inc.<br />
            This portal and any documents we provide are purely for your personal use as part of our coaching and training programs.<br />
            They are not to be shared, reproduced, or in any other way made publicly available.<br />
            For full details visit&nbsp;
            <Link
              href="https://my.gallup.com/_Home/ProductTerms"
              target="_blank"
              rel="noopener"
              underline="hover"
            >
              Gallup | Analytics and Tools to Transform Your Workspace - Product Terms of Use
            </Link>.
          </Typography>
        </Box>
      </Container>
      <Box
        sx={{
          width: "100%",
          marginTop: "64px",
          backgroundColor: "rgb(61, 61, 61)",
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            color: "#ffffff",
            display: isMobile ? "block" : "flex",
            gap: "64px",
          }}
        >
          <Box
              sx={{
                flex: 1,
                paddingTop: "30px",
                paddingBottom: "10px",
              }}
          >
            <Box sx={{ marginBottom: "20px" }}>
              <Typography variant="h6">OUR OFFICES</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
              }}
            >
              <Box sx={{ marginRight: "64px" }}>
                <Typography variant="body1">SINGAPORE</Typography>
                <Typography variant="body1">138 Robinson Road</Typography>
                <Typography variant="body1">#21-02 Oxley Tower Lobby 1</Typography>
                <Typography variant="body1">Singapore 068906</Typography>
              </Box>
              <Box>
                <Typography variant="body1">MALAYSIA</Typography>
                <Typography variant="body1">800 Jalan Sentul</Typography>
                <Typography variant="body1">D6 @ Sentul East</Typography>
                <Typography variant="body1">Unit 1-9</Typography>
                <Typography variant="body1">51000 Kuala Lumpur</Typography>
                <Typography variant="body1">Malaysia</Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 0,
              paddingTop: "30px",
              paddingBottom: "10px",
            }}
          >
            <Box sx={{ marginBottom: "20px" }}>
              <Typography variant="h6">FOLLOW US</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: "24px" }}>
              <Link
                href="http://www.mynewsdesk.com/sg/hong-bao-media"
                target="_blank"
                rel="noopener"
              >
                <img src={MyNewsDeskIcon} alt="MyNewsDesk" />
              </Link>
              <Link
                href="https://www.linkedin.com/company/hongbaomedia"
                target="_blank"
                rel="noopener"
              >
                <img src={LinkedInIcon} alt="LinkedIn" />
              </Link>
              <Link
                href="https://twitter.com/HongBaoMedia"
                target="_blank"
                rel="noopener"
              >
                <img src={TwitterIcon} alt="Twitter" />
              </Link>
              <Link
                href="https://www.facebook.com/hongbaomedia/"
                target="_blank"
                rel="noopener"
              >
                <img src={FacebookIcon} alt="Facebook" />
              </Link>
              <Link
                href="https://www.youtube.com/user/hongbaomedia"
                target="_blank"
                rel="noopener"
              >
                <img src={YoutubeIcon} alt="Youtube" />
              </Link>
              <Link
                href="https://instagram.com/hongbaomedia"
                target="_blank"
                rel="noopener"
              >
                <img src={InstagramIcon} alt="Instagram" />
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
      <Dialog
        open={isAvailableBundlesDialogOpen.value}
        TransitionComponent={Transition}
        keepMounted
        maxWidth="xl"
        onClose={handleAvailableBundlesDialogClose}
      >
        <DialogTitle>Available Bundles</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", margin: "8px 0" }}>
            {props.isError &&
              <Grid container direction="row" alignItems="center" justifyContent="center" spacing="2px">
                <Grid item>
                  <ErrorIcon color="error" fontSize="small" />
                </Grid>
                <Grid item>
                  <Typography variant="caption" color="error">{props.errorMessage}</Typography>
                </Grid>
              </Grid>
            }
            {props.isGettingBundles &&
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress color="primary" />
              </Box>
            }
            {!props.isGettingBundles && props.bundles &&
              <Container
                sx={{
                  padding: "0 !important",
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: "20px",
                  width: "100%",
                }}
              >
                {props.bundles.map((bundle, index) => (
                  <BundleCard key={index} bundle={bundle} />
                ))}
              </Container>
            }
          </Box>
        </DialogContent>
      </Dialog>
      <LiveChat license={LIVECHAT_LICENSE} />
    </>
  );
}

const mapState = (state) => {
  const {
    isGettingBundles,
    bundles,
    isError,
    errorMessage,
  } = state.cs;
  return {
    isGettingBundles,
    bundles,
    isError,
    errorMessage,
  };
};

const actionCreators = {
  getOrCreateVisitorId: visitorActions.getOrCreateId,
  getBundles: csActions.getBundles,
};

export default connect(mapState, actionCreators)(HomePage);
